import { useState, useEffect } from "react";
import { Box, Button, IconButton, Stack, Typography, CustomRadioGroup, LocationAutocomplete, MyLocationOutlinedIcon,
  FmdGoodOutlinedIcon, FmdBadOutlinedIcon
 } from "src/components";
import { useSnackbar, useIsMobile, useCurrentCoords } from "src/hooks";
import { updateProfilePrivacy, updateProfilePinnedLocation, useAuth, reverseGeocode } from "src/utils";
import { userPrivacyOptions } from "src/data";

export default function PrivacyRadioGroup() {
  const { user, setUser } = useAuth();
  const { openSnackbar } = useSnackbar();
  const isMobile = useIsMobile();

  const { coords, loading: geoLoading, fetchCoords } = useCurrentCoords();

  const extractCoords = (pinnedLocation) => {
    const coords = pinnedLocation?.coordinates;
    return coords ? { lat: coords[1], lng: coords[0] } : null;
  };

  const [locationPrivacy, setLocationPrivacy] = useState(user?.privacy ?? "Hidden");
  const [editPinnedMode, setEditPinnedMode] = useState(false);
  const [tempPinnedLocation, setTempPinnedLocation] = useState(() => extractCoords(user?.pinnedLocation));
  const [originalPinnedLocation, setOriginalPinnedLocation] = useState(() => extractCoords(user?.pinnedLocation));
  const [locationInputValue, setLocationInputValue] = useState(user?.pinnedLocation?.name);
  const [savingPinnedLocation, setSavingPinnedLocation] = useState(false);

  const hasPinned = !!user?.pinnedLocation;

  useEffect(() => {
    if (locationPrivacy === "Pinned" && !hasPinned && coords) {
      setTempPinnedLocation({ lat: coords.latitude, lng: coords.longitude });
    }
  }, [coords, locationPrivacy, hasPinned]);

  const handleUseCurrentLocation = async () => {
    try {
      const { latitude, longitude } = await fetchCoords();

      const result = await reverseGeocode(latitude, longitude);

      if (result && result.coords) {
        setTempPinnedLocation(result.coords);      
        setLocationInputValue(result.label || "");   
      }
    } catch (err) {
      console.error("Failed to get current location:", err);
      openSnackbar("Could not fetch your current location.", "error");
    }
  };

  const handlePrivacyChange = async (newValue) => {
    setLocationPrivacy(newValue);

    try {
      await updateProfilePrivacy(newValue);
      setUser((prevUser) => ({ ...prevUser, privacy: newValue }));
      openSnackbar("Privacy setting updated.", "success");

      if (newValue === "Pinned" && !hasPinned) {
        fetchCoords();
        setEditPinnedMode(true);
      } else {
        setEditPinnedMode(false);
      }
    } catch (err) {
      console.error(err);
      openSnackbar("Privacy update failed", "error");
    }
  };

  const handleSavePinned = async () => {
    try {
      const isClearing = tempPinnedLocation === null;

      const prev = user?.pinnedLocation;

      const hasChanged =
        (isClearing && prev !== null) ||
        (!isClearing &&
          (!prev ||
            locationInputValue !== prev.name ||
            tempPinnedLocation.lat !== prev.coordinates[1] ||
            tempPinnedLocation.lng !== prev.coordinates[0]));

      if (!hasChanged) {
        setEditPinnedMode(false);
        return;
      }

      setSavingPinnedLocation(true);

      if (isClearing) {
        await updateProfilePinnedLocation(null, null);
        setUser((prevUser) => ({ ...prevUser, pinnedLocation: null }));
        setOriginalPinnedLocation(null);
        setEditPinnedMode(false);
        openSnackbar("Pinned location removed.", "success");
        return;
      }

      const coordinates = [tempPinnedLocation?.lng, tempPinnedLocation?.lat];
      await updateProfilePinnedLocation(locationInputValue, coordinates);
      setUser((prevUser) => ({
        ...prevUser,
        pinnedLocation: {
          name: locationInputValue,
          coordinates: coordinates
        }
      }));
      setOriginalPinnedLocation(tempPinnedLocation);
      setEditPinnedMode(false);
      openSnackbar("Pinned location updated.", "success");
    } catch (err) {
      console.error(err);
      openSnackbar("Failed to save pinned location.", "error");
    } finally {
      setSavingPinnedLocation(false);
    }
  };

  const handleCancelEdit = () => {
    setTempPinnedLocation(originalPinnedLocation);
    setEditPinnedMode(false);
  };

  const minimalButtonStyle = {
    disableRipple: true,
    disableElevation: true,
    sx: {
      textTransform: "none",
      px: 1,
      py: 0.5,
      minWidth: "auto",
      '&:hover': {
        backgroundColor: "transparent",
      },
    },
  };

  return (
    <Box>
      <Typography variant="h6" mt={3}>Location Sharing</Typography>
      <Typography variant={isMobile ? "body2" : "body3"} color="text.secondary">
        Choose how others see your location on your profile.
      </Typography>

      <CustomRadioGroup
        selected={locationPrivacy}
        onChange={handlePrivacyChange}
        options={userPrivacyOptions}
      />

      {locationPrivacy === "Pinned" && (
        <Box mt={2}>
          {!editPinnedMode ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={0.5} flex={1} minWidth={0}>
                <FmdGoodOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography
                  variant={isMobile ? "subtitle1" : "body2"}
                  color="text.secondary"
                  fontWeight={600}
                  noWrap
                >
                  {user?.pinnedLocation?.name || "No location pinned"}
                </Typography>
              </Box>

              <Button
                variant="text"
                size="small"
                onClick={() => setEditPinnedMode(true)}
                {...minimalButtonStyle}
              >
                <Typography variant={isMobile ? "subtitle1" : "body2"} fontWeight={600}>
                  Edit
                </Typography>
              </Button>
            </Stack>
          ) : (
            <Box mt={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box flex={1}>
                  <LocationAutocomplete
                    value={locationInputValue}
                    currentCoords={tempPinnedLocation}
                    onSelect={(result) => {
                      if (result?.coords) {
                        setTempPinnedLocation(result.coords);
                        setLocationInputValue(result.label);
                      } else {
                        setTempPinnedLocation(null);
                        setLocationInputValue(""); // Reset if cleared
                      }
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={handleUseCurrentLocation}
                  disabled={geoLoading}
                  sx={{ ml: 1 }}
                >
                  <MyLocationOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={1} mt={1}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSavePinned}
                  loading={savingPinnedLocation}
                  disabled={locationInputValue === ""}
                  {...minimalButtonStyle}
                >
                  <Typography variant={isMobile ? "subtitle1" : "body2"} fontWeight={600}>
                    Save Pin
                  </Typography>
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleCancelEdit}
                  {...minimalButtonStyle}
                >
                  <Typography variant={isMobile ? "subtitle1" : "body2"} fontWeight={600}>
                    Cancel
                  </Typography>
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {locationPrivacy === "Hidden" && (<>
        <Box display="flex" alignItems="center" gap={0.5} flex={1} minWidth={0} mt={1} mb={0.5}>
            <FmdBadOutlinedIcon sx={{ fontSize: 18, color: "warning.main" }} />
            <Typography
              variant={isMobile ? "subtitle1" : "body2"}
              color="warning.main"
              fontWeight={600}
              lineHeight={1.2}
            >
              You're invisible to the community
            </Typography>
        </Box>
          <Typography
              variant={isMobile ? "body2" : "body3"}
              color="text.secondary"
              fontWeight={500}
              lineHeight={1.4}
            >
              Instead of being hidden, we recommend having a pinned location to anywhere in your current country.
            </Typography>
      </>)}

    </Box>
  );
}
